import { useCallback, useEffect, useState } from 'react';
import { LoaderCircle, Search } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { useAtomValue } from 'jotai';
import { uiStateAtomFiltersModalIsOpened } from '@rocco/ui/filter-sort/states/ui-states-filters-modal';
import { ReactComponent as CompassIcon } from '@rocco/icons/svg/compass.svg';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@rocco/components/popover';
import { Input } from '@rocco/components/input';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import type { FilterConfigModalItemDefault } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { LocationFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/location-filter-state';
import {
    formatLocation,
    formatPlaceType,
    getLocationFilterPlaceholder,
} from '@rocco/utils/format-location';

export const FiltersModalLocation = (
    props: FilterModalElementProps<
        FilterConfigModalItemDefault,
        LocationFilterState
    > & {
        /**
         * `sidebar` renders the 5.0 directory browse treatment: a filled input
         * (not the rounded pill) and a plain "Use my current location" link.
         */
        variant?: 'default' | 'sidebar';
        /** Extra classes for the input (e.g. dark-theme background). */
        inputClassName?: string;
        /** Extra classes for the "Use my current location" text. */
        currentLocationClassName?: string;
    },
) => {
    const {
        filterState,
        onFilterStateUpdate,
        variant = 'default',
        inputClassName,
        currentLocationClassName,
    } = props;
    const isSidebar = variant === 'sidebar';
    const { searchFilters } = useFiltersContext();
    const { locationService } = searchFilters;

    const filterOpened = useAtomValue(uiStateAtomFiltersModalIsOpened);

    const [selectedLocation, setSelectedLocation] = useState(
        filterState.selectedLocation ??
            locationService.currentLocation.location,
    );

    const [isEditing, setEditing] = useState(false);
    const [suggestions, setSuggestions] = useState<LocationPlace[]>([]);
    const [isSuggesting, setSuggesting] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [isSettingCurrentLocation, allowToSetCurrentLocation] =
        useState(false);

    const handleKeywordChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        if (event.currentTarget) {
            const value = event.currentTarget.value;

            setKeyword(value);

            if (value.trim().length > 2) {
                locationService.keywordLocations.onKeywordLocationsFetch(
                    value,
                    10,
                );
            }
        }
    };

    const resetLocationInput = useCallback(() => {
        locationService.keywordLocations.onKeywordResetPlaces();

        setKeyword('');
        setEditing(false);
        setSuggesting(false);
        setSuggestions([]);
        allowToSetCurrentLocation(false);
    }, [locationService.keywordLocations]);

    const handleSuggestionSelect = (location: LocationPlace) => {
        setSelectedLocation(location);
        resetLocationInput();
    };

    // update suggestions when keyword locations change
    useEffect(() => {
        if (locationService.keywordLocations.locations) {
            setSuggestions(locationService.keywordLocations.locations);
            setSuggesting(true);
            allowToSetCurrentLocation(false);
        }
    }, [locationService.keywordLocations.locations]);

    // set current location
    useEffect(() => {
        if (
            isSettingCurrentLocation &&
            locationService.currentLocation.location
        ) {
            setSelectedLocation(locationService.currentLocation.location);
            allowToSetCurrentLocation(false);
        }
    }, [locationService.currentLocation.location, isSettingCurrentLocation]);

    // update aggregations
    useEffect(() => {
        onFilterStateUpdate({
            selectedLocation: selectedLocation ?? null,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation]);

    // listen for filter modal close — reset suggestions/editing/keyword. The
    // sidebar variant lives outside the modal (filterOpened is always false),
    // so it must opt out or every keyword fetch would reset the input mid-type.
    useEffect(() => {
        if (!isSidebar && !filterOpened) {
            resetLocationInput();
        }
    }, [isSidebar, filterOpened, resetLocationInput]);

    return (
        <Popover
            open={isSuggesting && suggestions.length > 0}
            onOpenChange={setSuggesting}
        >
            <PopoverAnchor asChild>
                <div className="w-full">
                    <Input
                        color={isSidebar ? 'gray' : 'outline'}
                        text="small"
                        shape={isSidebar ? 'default' : 'rounded'}
                        className={
                            isSidebar
                                ? cn(
                                      'h-[2.625rem] rounded-[0.25rem] bg-[#f4f4f4] px-[0.5625rem] text-[1rem] tracking-[-0.01rem] placeholder:text-[#717171]',
                                      inputClassName,
                                  )
                                : undefined
                        }
                        placeholder={getLocationFilterPlaceholder(
                            locationService.country,
                        )}
                        role="combobox"
                        aria-expanded={isSuggesting && suggestions.length > 0}
                        aria-haspopup="listbox"
                        value={
                            isEditing
                                ? keyword
                                : formatLocation(selectedLocation)
                        }
                        showClearButton={true}
                        postfix={<Search className="size-4.5" />}
                        onChange={handleKeywordChange}
                        onFocus={() => setEditing(true)}
                        onBlur={() => {
                            setEditing(false);
                            setKeyword('');
                        }}
                        onClear={() => {
                            setKeyword('');
                            setSuggestions([]);
                            setSelectedLocation(null);
                        }}
                    />
                </div>
            </PopoverAnchor>

            <div
                className={cn(
                    'flex items-center gap-2',
                    isSidebar ? 'mt-2' : 'mt-4',
                )}
            >
                <div
                    className="flex cursor-pointer gap-2"
                    onClick={() => {
                        locationService.currentLocation.onCurrentLocationFetch();
                        allowToSetCurrentLocation(true);
                    }}
                >
                    {!isSidebar && <CompassIcon className="size-4.5" />}
                    <span
                        className={
                            isSidebar
                                ? cn(
                                      'text-[0.875rem] text-[#343434]',
                                      currentLocationClassName,
                                  )
                                : 'underline underline-offset-2'
                        }
                    >
                        Use my current location
                    </span>
                </div>

                {locationService.currentLocation.isLoading && (
                    <div className="flex-centered size-4 opacity-50">
                        <LoaderCircle className="size-4 animate-spin" />
                    </div>
                )}
            </div>

            <PopoverContent
                className={cn(
                    'min-w-54',
                    // Sidebar: compact rows and a capped, scrollable list so the
                    // suggestions don't tower over the narrow panel.
                    isSidebar &&
                        'max-h-[18rem] w-[var(--radix-popover-trigger-width)] min-w-0 overflow-y-auto',
                )}
                align="start"
                paddings="verticalSmall"
                role="listbox"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {suggestions.map((suggestion, index) => (
                    <div
                        key={index}
                        className={cn(
                            'cursor-pointer hover:bg-gray-100',
                            isSidebar ? 'px-3 py-1.5' : 'px-4 py-2',
                        )}
                        role="option"
                        onClick={() => handleSuggestionSelect(suggestion)}
                    >
                        <div className={isSidebar ? 'text-sm' : 'text-base'}>
                            {formatLocation(suggestion)}
                        </div>
                        <div
                            className={cn(
                                'text-gray-500',
                                isSidebar ? 'text-xs' : 'mt-0.5 text-sm',
                            )}
                        >
                            {formatPlaceType(
                                suggestion.placeType,
                                locationService.country,
                            )}
                        </div>
                    </div>
                ))}
            </PopoverContent>
        </Popover>
    );
};
